import { FontMetrics, getFontMetrics } from "../../util/font";
import { charWidth, defaultLineHeightFactor, lineWidth, textWidth, zip } from "../shared";
import { CanvasRegion, FontData, Region, TextboxElement } from "../../types";
import { Font } from "opentype.js";

const clampWidth = (lines: string[], currentWidth: number, fontScale: number, cache: FontMetrics): number => {
    return Math.max(charWidth(lines.join("\n"), fontScale, cache), currentWidth)
}

/**
 * Produce an array where text has been broken into lines that can
 * fit the given region with width = region.width and height = auto.
 */
const getLines = (
    region: CanvasRegion,
    text: string,
    fontSize: number,
    fontScale: number,
    cache: FontMetrics,
    lineHeight?: number,
): TraditionalTextResults => {
    const lh = (lineHeight || defaultLineHeightFactor) * cache.getApproximateHeight();
    const lines = text.split("\n");

    try {
        // Split each line into lines that are less wide than the region.
        const processedLines = lines.flatMap(line => splitLineUntilFit(line, region.width, fontScale, cache));
        const calculatedLines = processedLines.flatMap(li => li.lines);
        return {
            lines: calculatedLines,
            requiredHeight: processedLines.flatMap(li => li.lines).length * (fontScale * lh),
            fontSize: fontSize,
            requiredWidth: clampWidth(calculatedLines, region.width, fontScale, cache)
        };
    } catch (e) {
        const allChars = text.split('');
        return {
            lines: allChars,
            requiredHeight: allChars.length * (fontScale * lh),
            fontSize: fontSize,
            requiredWidth: clampWidth(allChars, region.width, fontScale, cache)
        };
    }
};

const splitLineUntilFit = (line: string, regionWidth: number, fontScale: number, cache: FontMetrics): { lines: string[], width: number } => {
    const initialWidth = textWidth(line, fontScale, cache);
    if (initialWidth <= regionWidth) {
        return { lines: [line], width: initialWidth };
    }

    const lineContainsSpace = line.indexOf(' ') > -1;
    if (!lineContainsSpace) {
        // No space, whole line is one word.
        if (line.length <= 1) {
            throw new Error(`Character ${line} is wider than region`);
        }
        const middle = Math.floor(line.length / 2);

        const splitA = splitLineUntilFit(line.slice(0, middle), regionWidth, fontScale, cache);
        const splitB = splitLineUntilFit(line.slice(middle), regionWidth, fontScale, cache);
        return { lines: [...splitA.lines, ...splitB.lines], width: Math.max(splitA.width, splitB.width) };
    }

    const allWords = line.split(' ');
    const lines: string[] = [];
    let maxWidth = -Infinity;

    let firstWordIndex = 0;
    while (firstWordIndex < allWords.length) {
        const firstWord = allWords[firstWordIndex];
        if (textWidth(firstWord, fontScale, cache) > regionWidth) {
            // First word is too big for one line.
            const split = splitLineUntilFit(firstWord, regionWidth, fontScale, cache);
            lines.push(...split.lines);
            maxWidth = Math.max(maxWidth, split.width);
            firstWordIndex++;
        } else {
            // Add words to temp line until we go over.
            const tempWords = [firstWord];
            maxWidth = Math.max(maxWidth, textWidth(firstWord, fontScale, cache));

            let lastWordIndex = firstWordIndex + 1;
            let fits = true;
            while (lastWordIndex < allWords.length && fits) {
                const nextWord = allWords[lastWordIndex];
                const wordsWidth = textWidth(`${tempWords.join(' ')} ${nextWord}`, fontScale, cache);
                if (wordsWidth <= regionWidth) {
                    tempWords.push(nextWord);
                    maxWidth = Math.max(maxWidth, wordsWidth);
                    lastWordIndex++;
                } else {
                    fits = false;
                }
            }
            lines.push(tempWords.join(' '));

            firstWordIndex = lastWordIndex;
        }
    }

    if (maxWidth < 0) {
        throw new Error(`No max width calculated for text: ${lines}.`);
    }
    return { lines, width: maxWidth };
};

export interface TraditionalTextResults {
    lines: string[];
    fontSize: number,
    requiredHeight: number;
    requiredWidth: number;
}

const calcFontSize = (
    lines: string[],
    cache: FontMetrics,
    font: Font,
    width: number,
    height: number,
    lineHeight?: number
): number => {
    const lh = (lineHeight ?? defaultLineHeightFactor) * cache.getApproximateHeight();
    const fontScale = 1 / font.unitsPerEm;

    const desiredWidth = lineWidth(lines, fontScale, cache);
    const desiredHeight = lines.length * lh * fontScale;

    const fontSizeW = width / desiredWidth;
    const fontSizeH = height / desiredHeight;
    return Math.min(fontSizeH, fontSizeW);
}

/**
 * Automatically fit the dimensions and font size of a textbox, based on how the textbox has been changed
 * @param updatedText The new input text.
 * @param updatedElement The element in its new, updated state.
 * @param previousElement The element before any changes.
 * @param lineHeight The line height of the textbox.
 * @returns A collection of changes that are required to correctly update the textbox.
 */
export const refitTextbox = (
    updatedText: string,
    updatedElement: TextboxElement,
    previousElement?: TextboxElement,
    lineHeight?: number,
): TraditionalTextResults => {
    const cache = getFontMetrics(updatedElement.fontData!.assetUrl);
    const font = cache.getFont();
    const fontScale = updatedElement.fontSize / font.unitsPerEm;

    if (!previousElement) {
        return getLines(updatedElement as unknown as CanvasRegion, updatedText, updatedElement.fontSize, fontScale, cache, lineHeight);
    }

    // fallbackLines is only used when not re-calculating the lines of text
    let fallbackLines: string[];
    if (!updatedElement.text) {
        fallbackLines = updatedElement.input?.split("\n") ?? [""];
    } else {
        fallbackLines = updatedElement.text.split("\n");
    }

    // layout has not changed
    if (previousElement.input === updatedText
        && updatedElement.width === previousElement.width
        && updatedElement.height === previousElement.height
        && updatedElement.lineHeight === previousElement.lineHeight) {
        return {
            lines: fallbackLines,
            requiredHeight: updatedElement.height,
            requiredWidth: clampWidth(fallbackLines, updatedElement.width, fontScale, cache),
            fontSize: updatedElement.fontSize
        }
    }

    const oneAxisChange = (updatedElement.width !== previousElement.width) !== (updatedElement.height !== previousElement.height);
    if (updatedElement.lineHeight === previousElement.lineHeight
        && !oneAxisChange
        && previousElement.input === updatedText) {
        return {
            lines: fallbackLines,
            requiredHeight: updatedElement.height,
            requiredWidth: clampWidth(fallbackLines, updatedElement.width, fontScale, cache),
            fontSize: calcFontSize(fallbackLines, cache, font, updatedElement.width, updatedElement.height, lineHeight)
        };
    } else {
        return getLines(updatedElement as unknown as CanvasRegion, updatedText, updatedElement.fontSize, fontScale, cache, lineHeight);
    }
}

/**
 * If a textbox has a fixed font size, it's width should be changed to fit the text.
 */
export const recalculateTextboxRegion = (
    currentRegion: Region,
    fontData: FontData,
    fontSize: number,
    textLines: string[],
    align?: string,
): Region => {
    const cache = getFontMetrics(fontData.assetUrl);
    const font = cache.getFont();

    let region = { ...currentRegion };
    const candidateRegion = { ...currentRegion };
    const requiredWidth = lineWidth(textLines, fontSize / font.unitsPerEm, getFontMetrics(fontData.assetUrl));

    if (requiredWidth && requiredWidth < region.width) {
        candidateRegion.width = requiredWidth;
        if (align === "left") {
            candidateRegion.left += Math.sin((region.rotation * Math.PI) / 360) * (region.width - requiredWidth);
            candidateRegion.top += (Math.sin((region.rotation * Math.PI) / 180) * (requiredWidth - region.width)) / 2;
        } else if (align === "right") {
            candidateRegion.left += region.width - requiredWidth;
        } else {
            candidateRegion.left += (region.width - requiredWidth) / 2;
        }
        if (
            textLines &&
            zip(textLines, textLines).every(([lineA, lineB]) => lineA === lineB)
        ) {
            region = candidateRegion;
        }
    }
    return region;
};
